import { useState, useEffect } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import { ProgressionTemplate, ProgressionContainer } from "../../libs/progression/types";
import { searchProgression } from "../../libs/progression/utils";
import { EditButton, DeleteButton, CloneButton } from "../Button";
import {
  useCreateProgressionTemplateMutation,
  useUpdateProgressionTemplateMutation,
  useCreateProgressionContainerMutation,
  useUpdateProgressionContainerMutation,
} from "../../libs/progression/api";
import { CreateProgressionModal } from "./CreateProgressionModal";
import { EditProgressionModal } from "./EditProgressionModal";
import { DeleteProgressionModal } from "./DeleteProgressionModal";
import { CreateContainerModal } from "./CreateContainerModal";
import { DeleteContainerModal } from "./DeleteContainerModal";
import { ViewModeToggle } from "./ViewModeToggle";
import { ContainerCard } from "./ContainerCard";
import { ProgressionCard } from "./ProgressionCard";
import { ColorPicker, DEFAULT_CONTAINER_COLOR } from "./ColorPicker";
import { ProgressionWeeksPreview } from "./ProgressionWeeksPreview";
import { ProgressionListRow } from "./ProgressionListRow";

const PAGE_SIZE = 10;

type Props = {
  templates: ProgressionTemplate[];
  containers: ProgressionContainer[];
};

export const ProgressionsTable: React.FC<Props> = ({ templates, containers }) => {
  const [viewMode, setViewMode] = usePersistedState<"grid" | "list">(
    "fitshape:progressions:viewMode",
    "grid"
  );
  const [activeContainerId, setActiveContainerId] = useState<number | null>(null);
  const [openModal, setOpenModal] = useState<string | undefined>();
  const [templateToEdit, setTemplateToEdit] = useState<ProgressionTemplate | undefined>();
  const [templateToDelete, setTemplateToDelete] = useState<ProgressionTemplate | undefined>();
  const [containerToDelete, setContainerToDelete] = useState<ProgressionContainer | undefined>();
  const [containerToRename, setContainerToRename] = useState<ProgressionContainer | undefined>();
  const [renameValue, setRenameValue] = useState("");
  const [renameColor, setRenameColor] = useState(DEFAULT_CONTAINER_COLOR);
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState(false);
  const [filteredResults, setFilteredResults] = useState<ProgressionTemplate[]>(templates);
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [createTemplate, { isLoading: isCloning }] = useCreateProgressionTemplateMutation();
  const [updateTemplate] = useUpdateProgressionTemplateMutation();
  const [createContainer] = useCreateProgressionContainerMutation();
  const [updateContainer] = useUpdateProgressionContainerMutation();

  const activeContainer = containers.find((c) => c.id === activeContainerId);

  // Auto-scroll when dragging near viewport edges
  useEffect(() => {
    const ZONE = 120;
    const MAX_SPEED = 18;
    let isDragging = false;
    let lastY = 0;
    let animFrame = 0;

    const loop = () => {
      if (!isDragging) return;
      const h = window.innerHeight;
      let speed = 0;
      if (lastY < ZONE) {
        // stronger the closer to the top
        speed = -MAX_SPEED * (1 - lastY / ZONE);
      } else if (lastY > h - ZONE) {
        speed = MAX_SPEED * (1 - (h - lastY) / ZONE);
      }
      if (speed !== 0) window.scrollBy(0, speed);
      animFrame = requestAnimationFrame(loop);
    };

    const onDragStart = () => {
      isDragging = true;
      animFrame = requestAnimationFrame(loop);
    };

    const onDragOver = (e: DragEvent) => {
      lastY = e.clientY;
    };

    const stopScroll = () => {
      isDragging = false;
      cancelAnimationFrame(animFrame);
    };

    window.addEventListener("dragstart", onDragStart);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragend", stopScroll);
    window.addEventListener("drop", stopScroll);
    return () => {
      window.removeEventListener("dragstart", onDragStart);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragend", stopScroll);
      window.removeEventListener("drop", stopScroll);
      cancelAnimationFrame(animFrame);
    };
  }, []);

  // Templates filtered by current container view
  const containerTemplates = activeContainerId != null
    ? templates.filter((t) => t.idContainer === activeContainerId)
    : templates;

  const handleClone = (template: ProgressionTemplate) => {
    const baseName = template.name.replace(/\(\d+\)$/, "").trimEnd();
    const escapedBase = baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(`^${escapedBase}\\((\\d+)\\)$`);
    const existingCounters = templates
      .map((t) => pattern.exec(t.name))
      .filter(Boolean)
      .map((m) => parseInt(m![1]));
    const counter =
      existingCounters.length > 0 ? Math.max(...existingCounters) + 1 : 1;
    createTemplate({
      name: `${baseName}(${counter})`,
      numWeeks: template.numWeeks,
      idContainer: template.idContainer,
      weeks: template.weeks,
    });
  };

  const handleCloneContainer = (container: ProgressionContainer) => {
    const baseName = container.name.replace(/\(\d+\)$/, "").trimEnd();
    const escapedBase = baseName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(`^${escapedBase}\\((\\d+)\\)$`);
    const existingCounters = containers
      .map((c) => pattern.exec(c.name))
      .filter(Boolean)
      .map((m) => parseInt(m![1]));
    const counter =
      existingCounters.length > 0 ? Math.max(...existingCounters) + 1 : 1;
    createContainer({ name: `${baseName}(${counter})` });
  };

  const handleRenameSubmit = () => {
    if (!containerToRename || !renameValue.trim()) return;
    setRenameSaving(true);
    setRenameError(false);
    updateContainer({ id: containerToRename.id, name: renameValue.trim(), color: renameColor })
      .unwrap()
      .then(() => {
        setContainerToRename(undefined);
        setRenameValue("");
        setRenameColor(DEFAULT_CONTAINER_COLOR);
        setOpenModal(undefined);
        setRenameSaving(false);
      })
      .catch(() => {
        setRenameSaving(false);
        setRenameError(true);
      });
  };

  const handleMoveToContainer = (template: ProgressionTemplate, containerId: number) => {
    updateTemplate({ ...template, idContainer: containerId });
  };

  const handleSearch = (value: string) => {
    searchProgression(containerTemplates, value, setSearchInput, setFilteredResults);
    setCurrentPage(1);
  };

  const displayed = (searchInput ? filteredResults : containerTemplates).slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const totalPages = Math.ceil(
    (searchInput ? filteredResults : containerTemplates).length / PAGE_SIZE
  );

  // ── Inside a container: show progressions ──
  if (activeContainerId != null) {
    return (
      <div className="p-4">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-4 text-sm">
          <button
            onClick={() => {
              setActiveContainerId(null);
              setSearchInput("");
              setCurrentPage(1);
            }}
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Progressioni
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-gray-700 dark:text-gray-300 font-medium">
            {activeContainer?.name}
          </span>
        </div>

        {/* Actions bar */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
          <div className="relative w-full md:w-1/2">
            <input
              type="text"
              placeholder="Cerca progressione..."
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              className="block w-full py-2 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />
            <button
              onClick={() => setOpenModal("create")}
              className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
            >
              Nuova progressione
            </button>
          </div>
        </div>

        {/* Grid or List view */}
        {viewMode === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayed.map((template) => (
              <ProgressionCard
                key={template.id}
                template={template}
                containerColor={activeContainer?.color}
                onClick={() => {
                  setTemplateToEdit(template);
                  setOpenModal("edit");
                }}
                onClone={() => handleClone(template)}
                onDelete={() => {
                  setTemplateToDelete(template);
                  setOpenModal("delete");
                }}
              />
            ))}
            {displayed.length === 0 && (
              <div className="col-span-full text-center text-gray-400 py-8">
                Nessuna progressione in questo contenitore.
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {displayed.length === 0 && (
              <div className="text-center text-gray-400 py-8">
                Nessuna progressione in questo contenitore.
              </div>
            )}
            {displayed.map((template) => {
              const listColor = activeContainer?.color || DEFAULT_CONTAINER_COLOR;
              return (
                <ProgressionListRow
                  key={template.id}
                  template={template}
                  color={listColor}
                  onClick={() => { setTemplateToEdit(template); setOpenModal("edit"); }}
                  onClone={() => handleClone(template)}
                  onDelete={() => { setTemplateToDelete(template); setOpenModal("delete"); }}
                  isCloning={isCloning}
                />
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-4 gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border dark:border-gray-600 dark:text-gray-300 disabled:opacity-40"
            >
              &laquo;
            </button>
            <span className="px-3 py-1 dark:text-gray-300">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded border dark:border-gray-600 dark:text-gray-300 disabled:opacity-40"
            >
              &raquo;
            </button>
          </div>
        )}

        <CreateProgressionModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          defaultContainerId={activeContainerId}
        />
        <EditProgressionModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          templateToUpdate={templateToEdit}
        />
        <DeleteProgressionModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          templateToDelete={templateToDelete}
        />
      </div>
    );
  }

  // ── Top-level: show containers ──
  return (
    <div className="p-4">
      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
        <div className="relative w-full md:w-1/2">
          <input
            type="text"
            placeholder="Cerca contenitore..."
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setCurrentPage(1);
            }}
            className="block w-full py-2 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-3">
          <ViewModeToggle viewMode={viewMode} setViewMode={setViewMode} />
          <button
            onClick={() => setOpenModal("createContainer")}
            className="text-white bg-primary-700 hover:bg-primary-800 focus:ring-4 focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 focus:outline-none"
          >
            Nuovo contenitore
          </button>
        </div>
      </div>

      {/* Filtered containers */}
      {(() => {
        const filteredContainers = searchInput
          ? containers.filter((c) =>
              c.name.toLowerCase().includes(searchInput.toLowerCase())
            )
          : containers;

        // Progressions without container (orphaned)
        const orphanedTemplates = templates.filter(
          (t) => !t.idContainer || !containers.some((c) => c.id === t.idContainer)
        );

        if (viewMode === "grid") {
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredContainers.map((container) => (
                <ContainerCard
                  key={container.id}
                  container={container}
                  templates={templates}
                  onClick={() => {
                    setActiveContainerId(container.id);
                    setSearchInput("");
                    setCurrentPage(1);
                  }}
                  onClone={() => handleCloneContainer(container)}
                  onDelete={() => {
                    setContainerToDelete(container);
                    setOpenModal("deleteContainer");
                  }}
                  onRename={() => {
                    setContainerToRename(container);
                    setRenameValue(container.name);
                    setRenameColor(container.color || DEFAULT_CONTAINER_COLOR);
                    setOpenModal("renameContainer");
                  }}
                  onDropProgression={(template) => handleMoveToContainer(template, container.id)}
                />
              ))}
              {filteredContainers.length === 0 && orphanedTemplates.length === 0 && (
                <div className="col-span-full text-center text-gray-400 py-8">
                  Nessun contenitore. Creane uno per organizzare le progressioni.
                </div>
              )}
              {/* Show orphaned progressions at the bottom */}
              {orphanedTemplates.length > 0 && (
                <>
                  <div className="col-span-full mt-4 mb-2">
                    <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Progressioni senza contenitore
                    </h3>
                  </div>
                  {orphanedTemplates.map((template) => (
                    <ProgressionCard
                      key={template.id}
                      template={template}
                      onClick={() => {
                        setTemplateToEdit(template);
                        setOpenModal("edit");
                      }}
                      onClone={() => handleClone(template)}
                      onDelete={() => {
                        setTemplateToDelete(template);
                        setOpenModal("delete");
                      }}
                    />
                  ))}
                </>
              )}
            </div>
          );
        }

        // List view
        return (
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th className="px-6 py-3">Nome</th>
                  <th className="px-6 py-3">Progressioni</th>
                  <th className="px-6 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredContainers.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-6 py-4 text-center text-gray-400">
                      Nessun contenitore trovato.
                    </td>
                  </tr>
                )}
                {filteredContainers.map((container) => {
                  const count = templates.filter((t) => t.idContainer === container.id).length;
                  return (
                    <tr
                      key={container.id}
                      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => {
                        setActiveContainerId(container.id);
                        setSearchInput("");
                        setCurrentPage(1);
                      }}
                    >
                      <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                          </svg>
                          {container.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">{count}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <CloneButton variant="icon" onClick={() => handleCloneContainer(container)} />
                          <EditButton
                            variant="icon"
                            onClick={() => {
                              setContainerToRename(container);
                              setRenameValue(container.name);
                              setOpenModal("renameContainer");
                            }}
                          />
                          <DeleteButton
                            variant="icon"
                            onClick={() => {
                              setContainerToDelete(container);
                              setOpenModal("deleteContainer");
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Modals */}
      <CreateContainerModal openModal={openModal} setOpenModal={setOpenModal} />
      <DeleteContainerModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        container={containerToDelete}
        templatesCount={
          containerToDelete
            ? templates.filter((t) => t.idContainer === containerToDelete.id).length
            : 0
        }
      />

      {/* Rename container modal */}
      {openModal === "renameContainer" && containerToRename && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Rinomina contenitore
            </h3>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              className="block w-full py-2 px-4 text-sm text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 focus:ring-blue-500 focus:border-blue-500 mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameSubmit();
              }}
            />
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                Colore
              </label>
              <ColorPicker value={renameColor} onChange={setRenameColor} />
            </div>
            {renameError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">
                Errore durante il salvataggio. Riprova.
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setOpenModal(undefined);
                  setContainerToRename(undefined);
                  setRenameError(false);
                }}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800"
              >
                Annulla
              </button>
              <button
                onClick={handleRenameSubmit}
                disabled={renameSaving}
                className="px-4 py-2 text-sm text-white bg-primary-700 hover:bg-primary-800 rounded-lg disabled:opacity-50"
              >
                {renameSaving ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit/delete progression modals for orphaned templates */}
      <EditProgressionModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        templateToUpdate={templateToEdit}
      />
      <DeleteProgressionModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        templateToDelete={templateToDelete}
      />
    </div>
  );
};
